
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';

import { Calculator } from 'lucide-react';
import SEO from '@/components/seo/seo';

const MortgageCalculator = () => {
  const [propertyPrice, setPropertyPrice] = useState<number>(1000000);
  const [downPayment, setDownPayment] = useState<number>(20);
  const [loanAmount, setLoanAmount] = useState<number>(800000);
  const [interestRate, setInterestRate] = useState<number>(4.5);
  const [loanTerm, setLoanTerm] = useState<number>(25);
  const [monthlyPayment, setMonthlyPayment] = useState<number>(0);

  // Calculate loan amount based on property price and down payment
  useEffect(() => {
    const calculatedLoanAmount = propertyPrice - (propertyPrice * (downPayment / 100));
    setLoanAmount(calculatedLoanAmount);
  }, [propertyPrice, downPayment]);

  // Calculate monthly payment
  useEffect(() => {
    const monthlyInterestRate = interestRate / 100 / 12;
    const numberOfPayments = loanTerm * 12;
    const monthlyPaymentAmount =
      loanAmount *
      (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) /
      (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);

    setMonthlyPayment(isNaN(monthlyPaymentAmount) ? 0 : monthlyPaymentAmount);
  }, [loanAmount, interestRate, loanTerm]);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <>
      <SEO
        title="Mortgage Calculator"
        description="Estimate your monthly mortgage payments based on property price, down payment, interest rate, and loan term."
        url="/mortgage-calculator"
        image="image/logo.png"
        keywords="Mortgage Calculator, Mortgage Payment Calculator, Mortgage Calculator UAE, Mortgage Calculator Abu Dhabi"
      />
      <div className="flex-grow py-16 px-4 bg-gray-50 mt-16">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-3">Mortgage Calculator</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Estimate your monthly mortgage payments based on property price, down payment,
              interest rate, and loan term.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Calculate Your Monthly Payment</CardTitle>
                  <CardDescription>Adjust the sliders to see how different factors affect your mortgage</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm font-medium">Property Price</label>
                      <span className="text-sm font-semibold">{formatCurrency(propertyPrice)}</span>
                    </div>
                    <Slider
                      value={[propertyPrice]}
                      min={500000}
                      max={10000000}
                      step={50000}
                      onValueChange={([value]) => setPropertyPrice(value)}
                      className="mb-6"
                    />
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPropertyPrice(1000000)}
                      >
                        1M
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPropertyPrice(2000000)}
                      >
                        2M
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPropertyPrice(5000000)}
                      >
                        5M
                      </Button>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm font-medium">Down Payment (%)</label>
                      <span className="text-sm font-semibold">{downPayment}% ({formatCurrency(propertyPrice * (downPayment / 100))})</span>
                    </div>
                    <Slider
                      value={[downPayment]}
                      min={5}
                      max={50}
                      step={1}
                      onValueChange={([value]) => setDownPayment(value)}
                      className="mb-6"
                    />
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDownPayment(10)}
                      >
                        10%
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDownPayment(20)}
                      >
                        20%
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDownPayment(30)}
                      >
                        30%
                      </Button>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm font-medium">Interest Rate (%)</label>
                      <span className="text-sm font-semibold">{interestRate}%</span>
                    </div>
                    <Slider
                      value={[interestRate]}
                      min={1}
                      max={10}
                      step={0.1}
                      onValueChange={([value]) => setInterestRate(value)}
                      className="mb-6"
                    />
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setInterestRate(3.5)}
                      >
                        3.5%
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setInterestRate(4.5)}
                      >
                        4.5%
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setInterestRate(5.5)}
                      >
                        5.5%
                      </Button>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm font-medium">Loan Term (Years)</label>
                      <span className="text-sm font-semibold">{loanTerm} years</span>
                    </div>
                    <Slider
                      value={[loanTerm]}
                      min={5}
                      max={30}
                      step={1}
                      onValueChange={([value]) => setLoanTerm(value)}
                      className="mb-6"
                    />
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setLoanTerm(15)}
                      >
                        15 yrs
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setLoanTerm(20)}
                      >
                        20 yrs
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setLoanTerm(25)}
                      >
                        25 yrs
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div>
              <Card className="bg-primary text-primary-foreground h-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5" />
                    <span>Payment Summary</span>
                  </CardTitle>
                  <CardDescription className="text-primary-foreground/80">
                    Based on your inputs
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <p className="text-sm text-primary-foreground/80">Loan Amount</p>
                    <h3 className="text-2xl font-bold">{formatCurrency(loanAmount)}</h3>
                  </div>

                  <div>
                    <p className="text-sm text-primary-foreground/80">Monthly Payment</p>
                    <h2 className="text-4xl font-bold">{formatCurrency(monthlyPayment)}</h2>
                  </div>

                  <div className="pt-4 border-t border-primary-foreground/20">
                    <p className="text-sm text-primary-foreground/80 mb-4">Ready to get pre-approved?</p>
                    <Button className="w-full bg-white text-primary hover:bg-white/90" disabled>
                      Apply for Pre-Approval
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className="mt-12 bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">Mortgage FAQs</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-1">What is a mortgage?</h3>
                <p className="text-muted-foreground">A mortgage is a loan specifically for purchasing real estate, where the property itself serves as collateral for the loan.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">How much down payment do I need in Dubai?</h3>
                <p className="text-muted-foreground">For expatriates, the minimum down payment is typically 25% for properties under AED 5 million, and 35% for properties over AED 5 million. UAE nationals usually need a minimum of 20%.</p>
              </div>
              <div>
                <h3 className="font-semibold mb-1">What factors affect my mortgage approval?</h3>
                <p className="text-muted-foreground">Key factors include your income, debt-to-income ratio, credit history, employment stability, and the property's value and condition.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MortgageCalculator;
